import AbstractItem from "../AbstractItem.js";

/**
 * Abstract superclass of all report columns.
 *
 * @abstract
 * @param {translatedString} [spec.customTitle] - Optional title that overrides the default one.
 * @param {boolean} [spec.visibleWhenNoData] - Set this to true when
 *   the column should be visible even if it has no data. Defaults to
 *   only showing the column if there is data to be shown.
 */
export default class AbstractColumn extends AbstractItem {
	constructor({ customTitle, visibleWhenNoData } = {}) {
		super(...arguments);

		this._customTitle = customTitle;
		this._visibleWhenNoData = visibleWhenNoData;
	}

	getTitle() {
		return this._customTitle || this._getDefaultTitle();
	}

	/**
	 * Set the data in the report for the column and a given `row`.
	 * Data is also set recursively to all sub-rows of `row`.
	 *
	 * @example
	 * col.setDataForRow({
	 *   row,
	 *   data: {
	 *     value: 3,
	 *     // Also set the data for children of row.
	 *     children: [{value: 1}, {value: 5}]
	 *   }
	 * });
	 * @param {ReportRow} row - The row for which data should be set.
	 * @param {object} data - Literal object containing the values.
	 */
	setDataForRow({ row, data }) {
		if (!data) {
			return;
		}

		this._report.setCellData({
			row,
			column: this,
			data: data.value,
		});
	}

	getLeaves() {
		return [this];
	}

	getSerializableLeaves() {
		return [this];
	}

	getFormatter({ defaultFormatter }) {
		return defaultFormatter;
	}

	isColumn() {
		return true;
	}

	isBudgetColumn() {
		return false;
	}

	isForecastColumn() {
		return false;
	}

	isEditableBudgetColumn() {
		return false;
	}

	hasData() {
		return this._report?.hasDataForColumn(this);
	}

	hasChoiceForBudget() {
		return false;
	}

	isPeriodColumn() {
		return false;
	}

	isPercentageColumn() {
		return false;
	}

	isReferenceForecastColumn() {
		return false;
	}

	isPredictedForecastColumn() {
		return false;
	}

	isCurrentPeriodForecastColumn() {
		return false;
	}

	hasPercentageFormat() {
		return this.isPercentageColumn();
	}

	accept(visitor) {
		return visitor.visitAbstractColumn(this);
	}

	shouldShowContent() {
		return this._visibleWhenNoData || this.hasData();
	}

	cssClass() {
		return "";
	}

	/**
	 * this method needs to be overriden in the corresponding subclasses and it will be used
	 * when a custom title will not be provided by the user
	 */
	_getDefaultTitle() {
		return this._subclassResponsibility();
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.customTitle = this._customTitle;
		properties.columnType = this.getType();

		return properties;
	}
}
